import { useEffect, useState } from "react";
import { Tooltip } from "react-tooltip";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { PlusIcon } from "@heroicons/react/24/solid";
import { Button, Field, Input, Label } from "@headlessui/react";
import { handleEnterKey } from "../../app/keyboardUtils";
import { useDebounceEffect } from "../../app/useDebounce";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import ModalFrame from "../../components/ModalFrame";
import RequiredField from "../../components/RequiredField";
import GradeSelector from "../../components/GradeSelector";
import SimpleListbox from "../../components/input/SimpleListbox";
import NamedEntity from "../../interfaces/NamedEntity";
import { useGetSchoolsMutation } from "../districts/districtsApiSlice";
import { GradeModel, ScoringLevelModel, useGetScoringLevelsQuery } from "../lookup/lookupApiSlice";
import {
	CreateCourseEditModel,
	useCreateCourseMutation,
	useGetCreateCourseQuery,
} from "./coursesApiSlice";

export default function AddCourseButton() {
	const [isDialogOpen, setIsDialogOpen] = useState(false);

	const handleClick = () => {
		setIsDialogOpen(true);
	};

	const handleDialogClose = () => setIsDialogOpen(false);

	return (
		<>
			<Button id="add-course-button" className="btn btn-neutral" onClick={handleClick}>
				<span className="flex items-center">
					<PlusIcon className="size-4 mr-2" /> Add Course
				</span>
			</Button>

			{isDialogOpen && (
				<AddCourseDialog isDialogOpen={isDialogOpen} onClose={handleDialogClose} />
			)}
		</>
	);
}

interface AddCourseDialogProps {
	isDialogOpen: boolean;
	onClose: () => void;
}

function AddCourseDialog(props: AddCourseDialogProps) {
	return (
		props.isDialogOpen && (
			<ModalFrame
				open={props.isDialogOpen}
				title="Add a New Course"
				overflow="overflow-visible"
				onClose={() => props.onClose()}>
				<AddCourseForm onClose={props.onClose} />
			</ModalFrame>
		)
	);
}

interface AddCourseFormProps {
	onClose: () => void;
}

function AddCourseForm(props: AddCourseFormProps) {
	const [showDistrictCode, setShowDistrictCode] = useState(false);
	const [districtCode, setDistrictCode] = useState("");
	const [name, setName] = useState("");
	const [school, setSchool] = useState<NamedEntity>();
	const [schools, setSchools] = useState<NamedEntity[]>([]);
	const [grade, setGrade] = useState<GradeModel>();
	const [scoringLevel, setScoringLevel] = useState<ScoringLevelModel>();

	const [errorMessage, setErrorMessage] = useState("");

	const scoringLevelResult = useGetScoringLevelsQuery();
	const scoringLevels = scoringLevelResult.data;

	const [callGetSchools] = useGetSchoolsMutation();
	const [callCreateCourse] = useCreateCourseMutation();

	const initCreateCourseResult = useGetCreateCourseQuery(undefined);
	const data = initCreateCourseResult.data;

	const isValid = validate();

	function validate() {
		if (!data) return false;
		if (data.isDateRangeRequired) return false;
		if (name == "") return false;
		if (!school) return false;
		if (!grade) return false;

		return true;
	}

	function defaultSchool(schools: NamedEntity[]) {
		if (schools.length == 1) return schools[0];
		return undefined;
	}

	useDebounceEffect(() => {
		if (!showDistrictCode) return;

		if (districtCode === "") {
			setSchool(undefined);
			setSchools([]);
		} else {
			callGetSchools({ districtCode })
				.unwrap()
				.then((result) => {
					if (Array.isArray(result)) {
						setSchools(result);
						setSchool(defaultSchool(result));
					}
				})
				.catch((error: any) => {
					setErrorMessage(error.data);
				});
		}
	}, [districtCode]);

	function handleDistrictCodeChange(event: React.ChangeEvent<HTMLInputElement>) {
		setDistrictCode(event.target.value);
	}

	function handleSchoolChange(school: NamedEntity) {
		setSchool(school);
	}

	function handleNameChange(event: React.ChangeEvent<HTMLInputElement>) {
		setName(event.target.value);
	}

	function handleGradeChange(grade: GradeModel) {
		const level = scoringLevels?.find((s) => s.scoringLevelId === grade.scoringLevelId);
		setGrade(grade);
		setScoringLevel(level);
	}

	function handleSave() {
		if (!isValid) return;
		const model: CreateCourseEditModel = {
			gradeId: grade?.gradeId!,
			name: name,
			school: school!,
			beginDate: undefined,
			endDate: undefined,
		};
		callCreateCourse(model)
			.unwrap()
			.then(() => {
				props.onClose();
			})
			.catch((error: any) => {
				setErrorMessage(error.data);
			});
	}

	function handleCancel() {
		props.onClose();
	}

	function toggleShowDistrictCode() {
		if (showDistrictCode && data) {
			setSchool(defaultSchool(data.currentSchools));
			setSchools(data.currentSchools);
		} else {
			setSchool(undefined);
			setSchools([]);
		}
		setShowDistrictCode(!showDistrictCode);
	}

	useEffect(() => {
		if (!data) return;
		if (data.currentSchools.length === 0) {
			setShowDistrictCode(true);
		} else {
			setSchool(defaultSchool(data.currentSchools));
			setSchools(data.currentSchools);
		}
	}, [data]);

	return (
		<div>
			<div className="flex justify-between gap-4 mt-2">
				<p>Enter course information in the fields below.</p>

				<p className="text-xs text-gray-600">
					<RequiredField className="text-base" /> - Denotes a required field
				</p>
			</div>
			<AlertMessage
				className="mt-2 text-sm"
				type={AlertLevel.Warning}
				message={errorMessage}
				showIcon={true}
			/>
			<div className="flex gap-4 mt-2">
				{showDistrictCode && (
					<Field className="w-1/2">
						<Label
							htmlFor="district-code"
							className="block font-semibold text-sm text-gray-900">
							District Code
						</Label>
						<Input
							type="text"
							id="district-code"
							className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600"
							onChange={handleDistrictCodeChange}></Input>
					</Field>
				)}
				<Field className="w-1/2">
					<Label htmlFor="school" className={`block font-semibold text-sm text-gray-900`}>
						School
						<RequiredField />
					</Label>
					<SchoolSelector
						schools={schools}
						school={school}
						prompt={
							showDistrictCode && schools.length === 0
								? "🠘 Enter a district code"
								: "Select a school..."
						}
						className="block w-full mt-1"
						onChange={handleSchoolChange}
					/>
				</Field>
				{!showDistrictCode && <Field className="w-1/2" />}
			</div>
			<div className="mt-1">
				{showDistrictCode ? (
					<p
						onClick={toggleShowDistrictCode}
						className="cursor-pointer text-green-900 text-sm">
						Don’t know the district code? Select a school used for a previous course.
					</p>
				) : (
					<p
						onClick={toggleShowDistrictCode}
						className="cursor-pointer text-green-900 text-sm">
						School not listed? Use a district code to find your school.
					</p>
				)}
			</div>

			<div className="mt-2">
				<Field className="w-full">
					<Label
						htmlFor="course-name"
						className="block font-semibold text-sm text-gray-900">
						Course Name
						<RequiredField />
					</Label>
					<Input
						type="text"
						id="course-name"
						className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600"
						onChange={handleNameChange}></Input>
				</Field>
			</div>

			<div className="flex gap-4 mt-2">
				<Field className="w-1/2">
					<Label
						htmlFor="course-grade"
						className="block font-semibold text-sm text-gray-900">
						Grade
						<RequiredField />
					</Label>
					<GradeSelector
						grade={grade}
						onChange={handleGradeChange}
						className="block w-full mt-1"
					/>
				</Field>
				<Field className="w-1/2">
					<div className="flex">
						<Label
							htmlFor="course-grade"
							className="block font-semibold text-sm text-gray-900">
							Scoring Level
						</Label>
						<ScoringLevelInfo className="ml-2" />
					</div>
					<Input
						type="text"
						id="scoring-level"
						disabled={true}
						readOnly={true}
						className="w-full mt-1 rounded-lg border-none bg-gray-200 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600"
						value={scoringLevel?.name ?? "unknown"}
					/>
				</Field>
			</div>

			<div className="mt-8 grid grid-cols-2 gap-3">
				<Button
					type="button"
					id="cancel-button"
					onClick={handleCancel}
					onKeyDown={(e) => handleEnterKey(e, handleCancel)}
					className="btn btn-neutral">
					Cancel
				</Button>
				<Button
					type="button"
					id="confirm-button"
					disabled={!isValid}
					onClick={handleSave}
					onKeyDown={(e) => handleEnterKey(e, handleSave)}
					className="btn btn-primary">
					Save
				</Button>
			</div>
		</div>
	);
}

function SchoolSelector(props: {
	schools: NamedEntity[];
	school?: NamedEntity;
	prompt: string;
	className: string;
	onChange: (school: NamedEntity) => void;
}) {
	const lookup = (value: number | string) => props.schools.find((s) => s.id == value);

	const options = props.schools.map((s) => ({ value: s.id, label: s.name }));

	const handleChange = (value: number | string) => {
		props.onChange(lookup(value)!);
	};

	return (
		<SimpleListbox
			id="schoolFilter"
			prompt={props.prompt}
			options={options}
			value={props.school?.id}
			className={`${props.className}`}
			onChange={(e) => handleChange(e.target.value)}
		/>
	);
}

function ScoringLevelInfo(props: { className?: string }) {
	const [isInfoVisible, setIsInfoVisible] = useState(false);

	const handleClick = () => {
		setIsInfoVisible(!isInfoVisible);
	};
	return (
		<>
			<InformationCircleIcon
				data-tooltip-id="scoring-level-info"
				className={`${props.className} h-5 text-gray-700 cursor-pointer`}
				onClick={handleClick}
			/>
			<Tooltip
				id="scoring-level-info"
				place="top"
				className="w-40"
				isOpen={isInfoVisible}
				openOnClick={true}>
				<div className="w-80">
					Course scoring level is banded, based on the course grade. When a student is
					added to a course, they will be assigned the course scoring level, but a teacher
					may adjust it using the <strong>Edit Student</strong> action.
				</div>
			</Tooltip>
		</>
	);
}
